import { useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";

const CATEGORIES = ["medicines", "equipment", "supplies", "consumables"];
const UNITS = ["pills", "bottles", "boxes", "units"];

export default function NewInventoryModal({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    item_name: "", item_category: "medicines", quantity: "0", unit: "units",
    reorder_level: "10", supplier: "", expiry_date: "", cost_per_unit: "0",
  });

  const submit = async () => {
    if (!f.item_name) { toast.error("Item name required"); return; }
    setSaving(true);
    try {
      await api.post("/ipd/inventory", {
        item_name: f.item_name,
        item_category: f.item_category,
        quantity: Number(f.quantity),
        unit: f.unit,
        reorder_level: Number(f.reorder_level),
        supplier: f.supplier,
        expiry_date: f.expiry_date || null,
        cost_per_unit: Number(f.cost_per_unit),
      });
      toast.success("Item added");
      setOpen(false);
      setF({ item_name: "", item_category: "medicines", quantity: "0", unit: "units",
             reorder_level: "10", supplier: "", expiry_date: "", cost_per_unit: "0" });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="ipd-new-item-btn">
          <Plus size={14} className="mr-1" /> New Item
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="ipd-new-item-modal">
        <DialogHeader><DialogTitle>New Inventory Item</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Item name *</Label>
            <Input value={f.item_name} onChange={(e) => setF({ ...f, item_name: e.target.value })}
                   data-testid="ipd-inv-name" /></div>
          <div><Label>Category</Label>
            <Select value={f.item_category} onValueChange={(v) => setF({ ...f, item_category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Unit</Label>
            <Select value={f.unit} onValueChange={(v) => setF({ ...f, unit: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Quantity</Label>
            <Input type="number" value={f.quantity}
                   onChange={(e) => setF({ ...f, quantity: e.target.value })} /></div>
          <div><Label>Reorder level</Label>
            <Input type="number" value={f.reorder_level}
                   onChange={(e) => setF({ ...f, reorder_level: e.target.value })} /></div>
          <div><Label>Supplier</Label>
            <Input value={f.supplier}
                   onChange={(e) => setF({ ...f, supplier: e.target.value })} /></div>
          <div><Label>Cost/unit (₹)</Label>
            <Input type="number" value={f.cost_per_unit}
                   onChange={(e) => setF({ ...f, cost_per_unit: e.target.value })} /></div>
          <div className="col-span-2"><Label>Expiry date</Label>
            <Input type="date" value={f.expiry_date}
                   onChange={(e) => setF({ ...f, expiry_date: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white"
                  data-testid="ipd-inv-submit">{saving ? "Saving…" : "Add Item"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
